import { useEffect, useRef, useState } from "react";
import { ApiError } from "@carepass/api-client";
import { Alert, Button, ChoiceControl, DetailList, Dialog, Field } from "../../../ui";
import { money } from "../../workspace/model";
import type { Schemas } from "../../workspace/api.generated";

export type ForcedTerminationMode = "NO_REFUND" | "BANK_REFUND";
export interface ForcedTerminationResult {
  terminationId: number;
  mode: ForcedTerminationMode;
  status: string;
  refund?: Schemas["RefundView"] | null;
}
export interface ForcedTerminationActions {
  onForcedPreview?: (mode: ForcedTerminationMode) => Promise<Schemas["TerminationPreviewView"]>;
  onForcedCurrent?: () => Promise<ForcedTerminationResult | undefined>;
  onForcedVerify?: (input: { bankCode: string; accountNumber: string; quoteToken: string }) => Promise<{ accountHolder: string; verificationToken: string }>;
  onForceTerminate?: (mode: ForcedTerminationMode, input: Schemas["RequestTerminationRequest"]) => Promise<ForcedTerminationResult>;
}
type Props = ForcedTerminationActions & {
  open: boolean;
  member: string;
  canTransfer: boolean;
  onBanks?: () => Promise<readonly { code: string; name: string }[]>;
  onClose: () => void;
  onRefresh: () => Promise<unknown>;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
};
export function ForcedTerminationDialog(props: Props) {
  const [mode, setMode] = useState<ForcedTerminationMode>("NO_REFUND");
  const [preview, setPreview] = useState<Schemas["TerminationPreviewView"]>();
  const [current, setCurrent] = useState<ForcedTerminationResult>();
  const [banks, setBanks] = useState<readonly { code: string; name: string }[]>([]);
  const [bank, setBank] = useState(""); const [account, setAccount] = useState("");
  const [verified, setVerified] = useState<{ key: string; accountHolder: string; verificationToken: string }>();
  const [holderConfirmed, setHolderConfirmed] = useState(false);
  const [reason, setReason] = useState(""); const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false); const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false); const [error, setError] = useState("");
  const generation = useRef(0); const executing = useRef(false);
  const key = [bank, account, preview?.quoteToken].join("|");
  const accountReady = verified?.key === key && holderConfirmed;
  const ready = preview?.allowed && preview.quoteToken?.length === 43 && !loading && !busy && !uncertain && !current;
  const canSubmit = ready && reason.trim().length > 0 && confirmed && (mode === "NO_REFUND" || (props.canTransfer && accountReady));
  const load = async () => {
    const id = ++generation.current; setLoading(true); setError(""); setPreview(undefined); setVerified(undefined); setHolderConfirmed(false); setConfirmed(false);
    try {
      const existing = await props.onForcedCurrent?.();
      if (id !== generation.current) return;
      setCurrent(existing); setUncertain(false);
      if (existing) return;
      const quote = await props.onForcedPreview?.(mode);
      if (!quote || (quote.allowed && !quote.quoteToken)) throw new Error();
      if (id !== generation.current) return;
      setPreview(quote);
      if (mode === "BANK_REFUND") {
        const options = await props.onBanks?.();
        if (id === generation.current) setBanks(options ?? []);
      }
    } catch (cause) { if (id === generation.current) setError(cause instanceof ApiError ? cause.message : "직권해지 정보를 불러오지 못했습니다. 다시 조회해 주세요."); }
    finally { if (id === generation.current) setLoading(false); }
  };
  useEffect(() => { if (props.open) void load(); return () => { generation.current++; }; }, [props.open, mode]);
  useEffect(() => { setVerified(undefined); setHolderConfirmed(false); setConfirmed(false); }, [bank, account]);
  const close = () => { if (busy || executing.current) return; props.onClose(); window.requestAnimationFrame(() => props.triggerRef.current?.focus()); };
  const verify = async () => {
    if (executing.current || !preview?.quoteToken || !props.onForcedVerify) return;
    executing.current = true; setBusy(true); setError(""); setVerified(undefined); setHolderConfirmed(false);
    const requestedKey = key;
    try {
      const result = await props.onForcedVerify({ bankCode: bank, accountNumber: account, quoteToken: preview.quoteToken });
      setVerified({ ...result, key: requestedKey });
    } catch (cause) { setError(cause instanceof ApiError ? cause.message : "예금주 조회에 실패했습니다. 은행·계좌번호를 확인해 주세요."); }
    finally { executing.current = false; setBusy(false); }
  };
  const execute = async () => {
    if (executing.current || !canSubmit || !props.onForceTerminate) return;
    executing.current = true; setBusy(true); setError("");
    try {
      const result = await props.onForceTerminate(mode, { reason: reason.trim(), confirmed: true, agreedToRefundPolicy: true,
        quoteToken: preview!.quoteToken!, bankCode: mode === "BANK_REFUND" ? bank : null,
        accountNumber: mode === "BANK_REFUND" ? account : null, accountHolder: mode === "BANK_REFUND" ? verified!.accountHolder : null,
        accountVerificationToken: mode === "BANK_REFUND" ? verified!.verificationToken : null });
      setCurrent(result); await props.onRefresh();
    } catch (cause) {
      setUncertain(true);
      setError(cause instanceof ApiError ? cause.message : "처리 결과를 확인하지 못했습니다. 최신 상태를 조회해 주세요.");
      try {
        const latest = await props.onForcedCurrent?.();
        if (latest) { setCurrent(latest); setUncertain(false); await props.onRefresh(); }
        else if (cause instanceof ApiError && !["network", "server"].includes(cause.kind)) { setUncertain(false); await load(); }
      } catch { /* Block duplicate commands until status is known. */ }
    } finally { executing.current = false; setBusy(false); }
  };
  return <Dialog className="admin-member-termination" open={props.open} title="관리자 직권해지" size="medium" tone="danger" onClose={close} closeDisabled={busy}
    primaryAction={current ? undefined : <Button size="small" variant="danger" disabled={!canSubmit} isLoading={busy} onClick={() => { void execute(); }}>{mode === "NO_REFUND" ? "미환불 해지" : "환불 후 해지"}</Button>}
    secondaryAction={<Button size="small" variant="quiet" disabled={busy} onClick={close}>{current ? "닫기" : "취소"}</Button>}>
    <DetailList items={[{ label: "회원", value: props.member }]} />
    {loading ? <p role="status">직권해지 정보를 조회하고 있습니다.</p> : null}
    {error ? <Alert persistent variant="danger" title="처리 상태 확인">{error}</Alert> : null}
    {current ? <Alert persistent variant={current.status === "COMPLETED" ? "success" : "warning"} title={current.status === "COMPLETED" ? "해지 완료" : "해지 처리 중"}>
      {current.mode === "NO_REFUND" ? "미환불로 해지했습니다. PG 취소와 송금은 실행하지 않았습니다." : current.status === "COMPLETED" ? "환불 송금과 해지가 완료되었습니다." : "신규 이용과 정기결제는 중단되었습니다. 환불 관리에서 결과 확인·재처리를 진행해 주세요. 환불 성공 후 해지가 완료됩니다."}
      {current.refund ? <p>환불액 {money(current.refund.customerTotalRefundAmount)} · {current.refund.refundNo}</p> : null}
    </Alert> : <div className="admin-command">
      <fieldset disabled={busy || loading || uncertain} className="admin-command__fieldset"><legend>처리 방식</legend>
        <ChoiceControl type="radio" name="forced-refund-mode" label="미환불" checked={mode === "NO_REFUND"} onChange={() => { setMode("NO_REFUND"); setPreview(undefined); setConfirmed(false); setVerified(undefined); setHolderConfirmed(false); }} />
        <ChoiceControl type="radio" name="forced-refund-mode" label="계좌환불" checked={mode === "BANK_REFUND"} onChange={() => { setMode("BANK_REFUND"); setPreview(undefined); setConfirmed(false); setVerified(undefined); setHolderConfirmed(false); }} />
      </fieldset>
      {preview && !preview.allowed ? <Alert persistent variant="warning" title="현재 직권해지할 수 없습니다">{preview.blockingReasonMessage}</Alert> : null}
      {mode === "NO_REFUND" ? <Alert variant="warning" title="미환불 해지">PG 결제 취소나 환불 송금 없이 회원을 해지합니다. 기존 결제 승인 이력은 유지됩니다.</Alert> : <>
        {!props.canTransfer ? <Alert variant="warning" title="환불 송금 권한이 필요합니다">권한 관리자에게 요청해 주세요.</Alert> : null}
        <fieldset disabled={!ready || busy || !props.canTransfer} className="admin-command__fieldset"><legend>환불계좌</legend>
          <Field compact control="select" label="환불 은행" value={bank} onChange={e => setBank(e.target.value)}><option value="">은행 선택</option>{banks.map(b => <option key={b.code} value={b.code}>{b.name}</option>)}</Field>
          <Field compact label="환불 계좌번호" inputMode="numeric" maxLength={20} value={account} onChange={e => setAccount(e.target.value.replace(/\D/g, ""))} />
          <Button size="small" variant="secondary" disabled={!bank || !/^[0-9]{8,20}$/.test(account) || busy} onClick={() => { void verify(); }}>예금주 조회</Button>
        </fieldset>
        {verified?.key === key ? <><p role="status">조회된 예금주: <strong>{verified.accountHolder}</strong></p><ChoiceControl label="조회된 예금주가 환불 대상자임을 확인했습니다" checked={holderConfirmed} disabled={busy} onChange={e => setHolderConfirmed(e.target.checked)} /></> : <p>계좌를 조회한 뒤 반환된 예금주를 직접 확인해 주세요. 미인증 회원은 회원명 일치로 자동 판정하지 않습니다.</p>}
        {accountReady && preview?.allowed ? <DetailList items={[{ label: "환불 금액", value: money(preview.customerTotalRefundAmount) }, { label: "계산 근거", value: preview.calculationText ?? "" }]} /> : null}
      </>}
      <Field compact control="textarea" label="직권해지 사유" maxLength={1000} value={reason} disabled={busy} onChange={e => setReason(e.target.value)} required />
      <ChoiceControl label={mode === "NO_REFUND" ? "환불 없이 해지하고 정기결제·신규 이용을 중단하는 것을 확인했습니다" : "환불 금액과 예금주를 확인했으며, 송금 성공 후 해지하는 것에 동의합니다"} checked={confirmed} disabled={!ready || busy || (mode === "BANK_REFUND" && !accountReady)} onChange={e => setConfirmed(e.target.checked)} />
    </div>}
    <Button size="small" variant="quiet" disabled={busy || loading} onClick={() => { void load(); }}>최신 상태 조회</Button>
  </Dialog>;
}
